import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy, Check, Trash2, Plus, AlertCircle, Eye, ExternalLink, Calendar, FileText } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { cn } from '@/lib/utils';
import { createShare, listSharesByReport, revokeShare } from '@/lib/api';
import type { ReportListItem, ShareToken } from '@/lib/api';

const DATE_FMT = new Intl.DateTimeFormat('vi-VN', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'Asia/Ho_Chi_Minh',
});

const DATETIME_FMT = new Intl.DateTimeFormat('vi-VN', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Asia/Ho_Chi_Minh',
});

function buildShareUrl(token: string): string {
  return `${window.location.origin}/share/${token}`;
}

function isExpired(expiresAt: string | null): boolean {
  if (!expiresAt) return false;
  return new Date(expiresAt).getTime() < Date.now();
}

export interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  report: Pick<ReportListItem, 'id' | 'title' | 'brand'>;
  /** When set, disables "Tạo link mới" + shows reason banner. Existing shares can still be
   *  managed/revoked. Used for draft status, all-brand reports, etc. */
  createDisabledReason?: string | null;
}

export function ShareDialog({ open, onOpenChange, report, createDisabledReason }: ShareDialogProps) {
  const [shares, setShares] = useState<ShareToken[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [description, setDescription] = useState('');
  const [expiresDays, setExpiresDays] = useState('30');
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ShareToken | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listSharesByReport(report.id);
      setShares(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, report.id]);

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      await createShare({
        report_id: report.id,
        description: description.trim() || undefined,
        expires_days: parseInt(expiresDays, 10),
      });
      setDescription('');
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async (token: string) => {
    try {
      await navigator.clipboard.writeText(buildShareUrl(token));
      setCopiedToken(token);
      toast.success('Đã copy link', { description: buildShareUrl(token) });
      setTimeout(() => setCopiedToken((cur) => (cur === token ? null : cur)), 2000);
    } catch {
      toast.error('Không copy được link');
    }
  };

  const handleRevokeRequest = (share: ShareToken) => {
    setRevokeTarget(share);
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    try {
      await revokeShare(revokeTarget.token);
      await refresh();
      toast.success('Đã vô hiệu hóa link', {
        description: 'Partner sẽ không xem được link này nữa.',
      });
    } catch (err) {
      toast.error('Không vô hiệu hóa được', {
        description: (err as Error).message,
      });
    } finally {
      setRevokeTarget(null);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            <span>Chia sẻ báo cáo · {report.title}</span>
          </DialogTitle>
          <DialogDescription>
            Tạo link cho partner xem <span className="font-semibold">snapshot frozen</span> của report
            <span className="font-semibold"> "{report.title}"</span>. Sau khi tạo, partner luôn xem cùng
            dữ liệu kể cả admin sửa report sau này.
          </DialogDescription>
        </DialogHeader>

        {/* Create new — disabled when report status / scope không cho phép share mới,
            nhưng vẫn cho admin manage (revoke) các link cũ ở section bên dưới. */}
        <section className={cn('min-w-0 rounded-lg border bg-muted/30 p-4', createDisabledReason && 'opacity-60')}>
          <h3 className="mb-2 text-sm font-semibold">Tạo link mới</h3>
          {createDisabledReason && (
            <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/[0.06] p-2 text-[11px] text-amber-900">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{createDisabledReason}</span>
            </div>
          )}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-1.5">
              <label htmlFor="share-desc" className="text-xs font-medium text-muted-foreground">
                Mô tả (tùy chọn)
              </label>
              <Input
                id="share-desc"
                placeholder="vd: Gửi anh Tuấn LG · meeting Q2"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={200}
                disabled={Boolean(createDisabledReason)}
              />
            </div>
            <div className="space-y-1.5 sm:w-32">
              <label htmlFor="share-exp" className="text-xs font-medium text-muted-foreground">
                Hết hạn
              </label>
              <Select value={expiresDays} onValueChange={setExpiresDays} disabled={Boolean(createDisabledReason)}>
                <SelectTrigger id="share-exp">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 ngày</SelectItem>
                  <SelectItem value="14">14 ngày</SelectItem>
                  <SelectItem value="30">30 ngày</SelectItem>
                  <SelectItem value="90">90 ngày</SelectItem>
                  <SelectItem value="180">6 tháng</SelectItem>
                  <SelectItem value="365">1 năm</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleCreate} disabled={creating || Boolean(createDisabledReason)}>
              <Plus className="h-4 w-4" />
              Tạo link
            </Button>
          </div>
        </section>

        {error && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-2.5 text-xs">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
            <span>{error}</span>
          </div>
        )}

        {/* List */}
        <section className="min-w-0">
          <h3 className="mb-2 text-sm font-semibold">
            Link đang active{' '}
            <span className="text-xs text-muted-foreground">({shares.length})</span>
          </h3>
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-16 w-full rounded-md" />
              <Skeleton className="h-16 w-full rounded-md" />
            </div>
          ) : shares.length === 0 ? (
            <div className="rounded-md border border-dashed bg-muted/20 p-6 text-center text-xs text-muted-foreground">
              Chưa có link nào. Tạo link đầu tiên ở trên.
            </div>
          ) : (
            <div className="max-h-72 space-y-2 overflow-y-auto overflow-x-hidden pr-1">
              {shares.map((s) => {
                const expired = isExpired(s.expires_at);
                const url = buildShareUrl(s.token);
                return (
                  <div
                    key={s.token}
                    className={cn(
                      'overflow-hidden rounded-md border bg-card p-3 text-xs',
                      expired && 'opacity-60'
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[11px] text-muted-foreground" title={url}>
                          {url}
                        </div>
                        {s.description && (
                          <div className="mt-1 break-words text-foreground">{s.description}</div>
                        )}
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Tạo {DATE_FMT.format(new Date(s.created_at))}
                          </span>
                          {s.expires_at && (
                            <span
                              className={cn(
                                'flex items-center gap-1',
                                expired && 'text-destructive font-medium'
                              )}
                            >
                              {expired ? 'Đã hết hạn' : `Hết hạn ${DATE_FMT.format(new Date(s.expires_at))}`}
                            </span>
                          )}
                          {s.last_viewed_at && (
                            <span className="flex items-center gap-1">
                              <Eye className="h-3 w-3" />
                              {DATETIME_FMT.format(new Date(s.last_viewed_at))}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => handleCopy(s.token)}
                          title="Copy link"
                        >
                          {copiedToken === s.token ? (
                            <Check className="h-3.5 w-3.5 text-cat-graphic" />
                          ) : (
                            <Copy className="h-3.5 w-3.5" />
                          )}
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => window.open(url, '_blank')}
                          title="Mở preview"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => handleRevokeRequest(s)}
                          title="Vô hiệu hóa"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </DialogContent>
    </Dialog>

    <ConfirmDialog
      open={!!revokeTarget}
      onOpenChange={(o) => !o && setRevokeTarget(null)}
      title="Vô hiệu hóa link share?"
      description={
        <>
          Partner sẽ <strong className="text-destructive">không xem được link này nữa</strong>.
          {revokeTarget?.description && (
            <span className="mt-2 block text-foreground">
              Mô tả: <span className="font-medium">{revokeTarget.description}</span>
            </span>
          )}
        </>
      }
      confirmText="Vô hiệu hóa"
      cancelText="Hủy"
      variant="destructive"
      onConfirm={confirmRevoke}
    />
    </>
  );
}
